import {SHEET_COPY} from './sheet-copy.ts';
import {ENGINEERING_COPY} from './engineering-copy.ts';
import {DEBUG_DELIVERY_COPY} from './debug-delivery-copy.ts';
const en = {
  ...SHEET_COPY.en,
  ...ENGINEERING_COPY.en,
  ...DEBUG_DELIVERY_COPY.en,
  showcase: 'Demo studio',
  homeLink: 'PlayerOne — Home', overview: 'Overview', workspace: 'Workspace', management: 'Management',
  overviewNote: 'Review work, resolve exceptions, and keep the counter moving.',
  refresh: 'Refresh', refreshing: 'Refreshing…', updated: 'Updated {{time}}',
  refreshFailed: 'Could not refresh. Previously loaded data may be out of date.',
  loadFailed: 'Could not load this data', loadFailedNote: 'Check your connection and try again. If the problem continues, share the reference below with support.',
  unavailable: 'Unavailable', retry: 'Try again', loading: 'Loading…',
  shift: 'Your review activity', shiftScope: 'Today’s decisions · server day', reviewed: 'Reviewed episodes',
  approval: 'Approval rate', approvalNote: '{{approved}} of {{decided}} decisions', target: 'Shift target: {{count}}',
  attention: 'Work to pick up', attentionNote: 'Current queue and exceptions requiring a person.',
  reviewQueue: 'Ready for review', reviewNote: 'Open the review workspace to take the next available episode.',
  reviewAction: 'Open review', unresolved: 'Needs attention', unresolvedNote: 'Inspect blocked episodes and resolve their recorded exceptions.',
  resolveAction: 'View exceptions', handover: 'Record a handover', settlement: 'View settlement',
  tasks: 'Tasks', tasksNote: 'Work defined for collectors.', manageTasks: 'Manage tasks',
  task: 'Task', state: 'Status', claimants: 'Current claimants', taskEmpty: 'No tasks have been created yet.',
  taskEmptyNote: 'Administrators can create a task in Back office. Its records will appear here.',
  taskSubset: 'Showing {{shown}} of {{total}} tasks', recentNote: 'Your latest recorded review decisions.',
  profile: 'Profile & settings', profileNote: 'Your identity, recorded actions, and preferences for this browser.',
  identity: 'Account', identityReadOnly: 'Identity and permissions are managed by your administrator.',
  operator: 'Operator reference', role: 'Role', centre: 'Upload centre', region: 'Region', status: 'Status',
  noCentre: 'No upload centre assigned', unknownIdentity: 'Account details unavailable',
  administrator: 'Administrator', finance: 'Finance', reviewer: 'Reviewer', centre_operator: 'Centre operator',
  active: 'Active', retired: 'Retired', preferences: 'Preferences', appearance: 'Appearance',
  appearanceNote: 'Choose a light or dark workspace, or follow this device.',
  light: 'Light', dark: 'Dark', system: 'Use device setting', language: 'Language',
  languageNote: 'Used throughout PlayerOne on this browser.',
  photo: 'Profile photo', photoNote: 'Saved only in this browser for this account. It is not uploaded.',
  choosePhoto: 'Choose photo', resetPhoto: 'Remove photo', photoError: 'Choose a PNG, JPEG or WebP image smaller than 1 MB.',
  storageError: 'This browser could not save the preference. Please try again.',
  signOut: 'Sign out', signingOut: 'Signing out…', signOutNote: 'End this browser’s operator and machine session.',
  signOutError: 'Could not sign out. Your session may still be active. Try again.',
  activity: 'Recorded actions', activityRange: '{{from}} – {{to}} · {{timezone}}',
  activityScope: 'Your recorded operator actions. Sign-ins and failed sign-ins are excluded.',
  activitySummary: '{{count}} actions across {{days}} active days', activityEmpty: 'No recorded actions in this period.',
  activityEmptyNote: 'Actions you record in PlayerOne will appear here after they are saved.',
  dayActions: '{{date}}: {{count}} recorded actions', calendarHelp: 'Select a day for its count. Use arrow keys to move between days.',
  fewer: 'Fewer', more: 'More', selectedDay: 'Selected day', actionsCount: '{{count}} actions',
  guideSummary: 'Your recorded review work for today. Each figure keeps its scope; unavailable data is never shown as zero.',
  guideWork: 'Open the review queue or inspect exceptions. These actions lead to the existing workspaces.',
  guideRecent: 'Recent decisions remain attached to their episode, effective duration and settlement amount.',
};

export const WORKSPACE_COPY: Record<'en' | 'vi' | 'zh', Record<keyof typeof en, string>> = {
  en,
  vi: {
    ...SHEET_COPY.vi,
    ...ENGINEERING_COPY.vi,
    ...DEBUG_DELIVERY_COPY.vi,
    showcase: 'Phòng thử nghiệm',
    homeLink: 'PlayerOne — Trang chủ', overview: 'Tổng quan', workspace: 'Không gian làm việc', management: 'Quản lý',
    overviewNote: 'Duyệt dữ liệu, xử lý ngoại lệ và theo dõi công việc tại quầy.',
    refresh: 'Làm mới', refreshing: 'Đang làm mới…', updated: 'Cập nhật lúc {{time}}',
    refreshFailed: 'Không thể làm mới. Dữ liệu đã tải trước đó có thể chưa cập nhật.',
    loadFailed: 'Không thể tải dữ liệu này', loadFailedNote: 'Kiểm tra kết nối và thử lại. Nếu lỗi vẫn tiếp diễn, hãy gửi mã tham chiếu bên dưới cho bộ phận hỗ trợ.',
    unavailable: 'Chưa có dữ liệu', retry: 'Thử lại', loading: 'Đang tải…',
    shift: 'Hoạt động duyệt của bạn', shiftScope: 'Kết quả hôm nay · ngày theo máy chủ', reviewed: 'Phiên đã duyệt',
    approval: 'Tỷ lệ đạt', approvalNote: '{{approved}} trên {{decided}} kết quả', target: 'Mục tiêu ca: {{count}}',
    attention: 'Công việc cần xử lý', attentionNote: 'Hàng chờ hiện tại và ngoại lệ cần người xử lý.',
    reviewQueue: 'Sẵn sàng duyệt', reviewNote: 'Mở không gian duyệt để nhận phiên tiếp theo.',
    reviewAction: 'Mở trang duyệt', unresolved: 'Cần chú ý', unresolvedNote: 'Kiểm tra phiên bị chặn và xử lý ngoại lệ đã ghi nhận.',
    resolveAction: 'Xem ngoại lệ', handover: 'Ghi nhận bàn giao', settlement: 'Xem quyết toán',
    tasks: 'Nhiệm vụ', tasksNote: 'Công việc dành cho người thu thập.', manageTasks: 'Quản lý nhiệm vụ',
    task: 'Nhiệm vụ', state: 'Trạng thái', claimants: 'Người đang nhận', taskEmpty: 'Chưa có nhiệm vụ nào được tạo.',
    taskEmptyNote: 'Quản trị viên có thể tạo nhiệm vụ trong trang Quản trị. Thông tin sẽ xuất hiện tại đây.',
    taskSubset: 'Hiển thị {{shown}} trên {{total}} nhiệm vụ', recentNote: 'Các kết quả duyệt gần nhất do bạn ghi nhận.',
    profile: 'Hồ sơ và cài đặt', profileNote: 'Danh tính, hoạt động đã ghi nhận và tùy chọn trên trình duyệt này.',
    identity: 'Tài khoản', identityReadOnly: 'Danh tính và quyền truy cập do quản trị viên quản lý.',
    operator: 'Mã nhân viên', role: 'Vai trò', centre: 'Trung tâm tải lên', region: 'Khu vực', status: 'Trạng thái',
    noCentre: 'Chưa được gán trung tâm', unknownIdentity: 'Chưa tải được thông tin tài khoản',
    administrator: 'Quản trị viên', finance: 'Tài chính', reviewer: 'Nhân viên duyệt', centre_operator: 'Nhân viên trung tâm',
    active: 'Đang hoạt động', retired: 'Đã ngừng hoạt động', preferences: 'Tùy chọn', appearance: 'Giao diện',
    appearanceNote: 'Chọn giao diện sáng, tối hoặc theo thiết bị.',
    light: 'Sáng', dark: 'Tối', system: 'Theo thiết bị', language: 'Ngôn ngữ',
    languageNote: 'Áp dụng trên PlayerOne trong trình duyệt này.',
    photo: 'Ảnh hồ sơ', photoNote: 'Chỉ lưu trong trình duyệt này cho tài khoản này. Ảnh không được tải lên máy chủ.',
    choosePhoto: 'Chọn ảnh', resetPhoto: 'Xóa ảnh', photoError: 'Chọn ảnh PNG, JPEG hoặc WebP nhỏ hơn 1 MB.',
    storageError: 'Trình duyệt không lưu được tùy chọn. Vui lòng thử lại.',
    signOut: 'Đăng xuất', signingOut: 'Đang đăng xuất…', signOutNote: 'Kết thúc phiên nhân viên và máy trên trình duyệt này.',
    signOutError: 'Không thể đăng xuất. Phiên có thể vẫn còn hoạt động. Hãy thử lại.',
    activity: 'Hoạt động đã ghi nhận', activityRange: '{{from}} – {{to}} · {{timezone}}',
    activityScope: 'Hoạt động của chính bạn được ghi nhận trong hệ thống. Không tính đăng nhập và đăng nhập thất bại.',
    activitySummary: '{{count}} hoạt động trong {{days}} ngày', activityEmpty: 'Chưa có hoạt động trong khoảng thời gian này.',
    activityEmptyNote: 'Hoạt động bạn ghi nhận trong PlayerOne sẽ xuất hiện tại đây sau khi được lưu.',
    dayActions: '{{date}}: {{count}} hoạt động đã ghi nhận', calendarHelp: 'Chọn một ngày để xem số hoạt động. Dùng phím mũi tên để chuyển ngày.',
    fewer: 'Ít hơn', more: 'Nhiều hơn', selectedDay: 'Ngày đã chọn', actionsCount: '{{count}} hoạt động',
    guideSummary: 'Công việc duyệt bạn đã ghi nhận hôm nay. Mỗi số liệu có phạm vi rõ ràng; dữ liệu chưa có không được hiển thị thành số không.',
    guideWork: 'Mở hàng chờ duyệt hoặc kiểm tra ngoại lệ. Các thao tác dẫn đến không gian làm việc hiện có.',
    guideRecent: 'Mỗi kết quả gần đây gắn với phiên, thời lượng hiệu dụng và số tiền quyết toán.',
  },
  zh: {
    ...SHEET_COPY.zh,
    ...ENGINEERING_COPY.zh,
    ...DEBUG_DELIVERY_COPY.zh,
    showcase: '演示工作室',
    homeLink: 'PlayerOne — 首页', overview: '概览', workspace: '工作区', management: '管理',
    overviewNote: '审核数据、处理异常，跟进柜台工作。',
    refresh: '刷新', refreshing: '正在刷新…', updated: '更新于 {{time}}',
    refreshFailed: '无法刷新。此前加载的数据可能已过时。',
    loadFailed: '无法加载数据', loadFailedNote: '请检查网络连接后重试。如果问题持续，请向支持人员提供下方的参考编号。',
    unavailable: '暂无数据', retry: '重试', loading: '加载中…',
    shift: '你的审核活动', shiftScope: '今日判定 · 服务器日期', reviewed: '已审核片段',
    approval: '通过率', approvalNote: '{{decided}} 次判定中 {{approved}} 次通过', target: '班次目标：{{count}}',
    attention: '待处理工作', attentionNote: '当前队列及需要人工处理的异常。',
    reviewQueue: '待审核', reviewNote: '打开审核工作区，领取下一个可审核片段。',
    reviewAction: '打开审核', unresolved: '需要关注', unresolvedNote: '检查受阻片段，处理已记录的异常。',
    resolveAction: '查看异常', handover: '登记交接', settlement: '查看结算',
    tasks: '任务', tasksNote: '为采集者定义的工作。', manageTasks: '管理任务',
    task: '任务', state: '状态', claimants: '当前领取人数', taskEmpty: '尚未创建任务。',
    taskEmptyNote: '管理员可在后台创建任务，任务记录将显示在这里。',
    taskSubset: '显示 {{total}} 个任务中的 {{shown}} 个', recentNote: '你最近记录的审核判定。',
    profile: '个人资料与设置', profileNote: '你的身份、已记录的操作，以及此浏览器的偏好。',
    identity: '账户', identityReadOnly: '身份与权限由管理员管理。',
    operator: '操作员编号', role: '角色', centre: '上传中心', region: '区域', status: '状态',
    noCentre: '未分配上传中心', unknownIdentity: '账户详情暂时不可用',
    administrator: '管理员', finance: '财务', reviewer: '审核员', centre_operator: '中心操作员',
    active: '正常', retired: '已停用', preferences: '偏好设置', appearance: '外观',
    appearanceNote: '选择浅色、深色或跟随设备设置。',
    light: '浅色', dark: '深色', system: '跟随设备', language: '语言',
    languageNote: '应用于此浏览器中的 PlayerOne。',
    photo: '头像', photoNote: '仅为此账户保存在当前浏览器中，不会上传。',
    choosePhoto: '选择照片', resetPhoto: '移除照片', photoError: '请选择小于 1 MB 的 PNG、JPEG 或 WebP 图片。',
    storageError: '此浏览器无法保存偏好，请重试。',
    signOut: '退出登录', signingOut: '正在退出…', signOutNote: '结束此浏览器中的操作员及设备会话。',
    signOutError: '无法退出登录，会话可能仍有效，请重试。',
    activity: '已记录的操作', activityRange: '{{from}} – {{to}} · {{timezone}}',
    activityScope: '仅统计你已记录的操作，不包含登录及登录失败事件。',
    activitySummary: '{{days}} 个活跃日内共 {{count}} 次操作', activityEmpty: '此期间尚无操作记录。',
    activityEmptyNote: '你在 PlayerOne 中保存的操作会显示在这里。',
    dayActions: '{{date}}：{{count}} 次已记录的操作', calendarHelp: '选择日期查看数量，使用方向键切换日期。',
    fewer: '少', more: '多', selectedDay: '所选日期', actionsCount: '{{count}} 次操作',
    guideSummary: '你今天记录的审核工作。每项数值均注明范围，无法获取的数据不会显示为零。',
    guideWork: '打开审核队列或检查异常，进入现有工作区。',
    guideRecent: '最近的判定均对应具体片段、有效时长及结算金额。',
  },
};
